import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { projects, corporateStats } from "../data/projects";
import { FadeUp, MaskedLines } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ClientLogos from "../components/ClientLogos";

const spans = ["md:col-span-7 md:row-span-2", "md:col-span-5", "md:col-span-5"];

const copy = {
  corporate: {
    eyebrow: "Haus Ov Harii · Corporate",
    title: ["Corporate"],
    lead: "Identity, campaign, communications and document design for global institutions, delivered inside exacting brand systems without losing the human thread. Clients include JP Morgan & Chase, Cathay Pacific, Orica and Valiant Integrated Services.",
    marquee: ["Identity", "Campaign", "Communications", "Brand Systems", "Editorial", "Packaging"],
  },
  creative: {
    eyebrow: "Haus Ov Harii · Creative",
    title: ["Creative"],
    lead: "Illustration-led identity, packaging and image-making for culture, music and hospitality. Expressive work with a brutalist spine.",
    marquee: ["Illustration", "Identity", "Packaging", "Music", "Culture", "Hospitality"],
  },
};

function CorporateRow({ p, i }) {
  return (
    <FadeUp delay={i * 0.08}>
      <Link
        to={`/corporate/${p.slug}`}
        data-testid={`project-card-${p.slug}`}
        className="group grid gap-4 border-b border-white/10 py-10 transition-colors duration-500 hover:bg-white/[0.03] md:grid-cols-12 md:items-center md:px-4"
      >
        <span className="font-mono text-xs text-[#616775] md:col-span-1">
          {String(i + 1).padStart(2, "0")}
        </span>
        <div className="md:col-span-6">
          <h2 className="text-2xl font-light tracking-tight transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl">
            {p.title}
          </h2>
          <p className="mt-2 text-sm text-[#9EA3B0]">{p.client}</p>
        </div>
        <div className="flex flex-wrap gap-2 md:col-span-4">
          {p.tags.map((t) => (
            <span key={t} className="border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EA3B0]">
              {t}
            </span>
          ))}
        </div>
        <span className="hidden justify-self-end text-[#616775] transition-all duration-500 group-hover:translate-x-1 group-hover:text-umber md:block">
          <ArrowUpRight size={22} />
        </span>
      </Link>
    </FadeUp>
  );
}

function CreativeCard({ p, i }) {
  const ratios = ["aspect-[4/5]", "aspect-[4/3]", "aspect-[4/3]"];
  return (
    <FadeUp delay={Math.min(i, 5) * 0.08} className="h-full">
      <Link
        to={`/creative/${p.slug}`}
        data-testid={`project-card-${p.slug}`}
        className="group relative block h-full overflow-hidden border border-white/10 bg-[#0B0C0E]"
      >
        <div className={`${ratios[i % 3]} relative h-full w-full overflow-hidden md:aspect-auto`}>
          <img
            src={p.heroImage}
            alt={`${p.title}, ${p.client}`}
            loading="lazy"
            style={p.focal ? { objectPosition: p.focal } : undefined}
            className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E]/90 via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6">
          <p className="eyebrow mb-2">{p.client} · {p.year}</p>
          <h2 className="text-2xl font-light tracking-tight sm:text-3xl">{p.title}</h2>
        </div>
        <div className="absolute right-4 top-4 flex gap-2">
          {p.tags.slice(0, 2).map((t) => (
            <span key={t} className="bg-[#0B0C0E]/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 backdrop-blur">
              {t}
            </span>
          ))}
        </div>
      </Link>
    </FadeUp>
  );
}

export default function RouteHome({ route }) {
  const c = copy[route];
  const list = projects.filter((p) => p.route === route);
  const [tag, setTag] = useState("All");
  const counts = list.flatMap((p) => p.tags).reduce((m, t) => ({ ...m, [t]: (m[t] || 0) + 1 }), {});
  const tags = ["All", ...Object.keys(counts).filter((t) => counts[t] >= 2).sort()];
  const shown = tag === "All" ? list : list.filter((p) => p.tags.includes(tag));

  useEffect(() => {
    document.title = `${route === "corporate" ? "Corporate" : "Creative"} · Haus Ov Harii`;
  }, [route]);

  return (
    <div className="px-6 pt-32 lg:px-12" data-testid={`${route}-home`}>
      <header className="pb-16">
        <FadeUp>
          <p className="eyebrow mb-6">{c.eyebrow}</p>
        </FadeUp>
        <h1 className="text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
          <MaskedLines lines={c.title} delay={0.1} />
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#9EA3B0] sm:text-lg">
            {c.lead}
          </p>
        </FadeUp>
      </header>

      <Marquee items={c.marquee} className="-mx-6 lg:-mx-12" />

      {route === "corporate" ? (
        <>
          <FadeUp className="grid grid-cols-2 gap-px border-y border-white/10 bg-white/10 md:grid-cols-4">
            {corporateStats.map((s) => (
              <div key={s.label} className="bg-[#0B0C0E] p-8">
                <p className="whitespace-nowrap text-2xl font-light tracking-tight text-umber sm:text-4xl">{s.value}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#616775]">{s.label}</p>
              </div>
            ))}
          </FadeUp>
          <ClientLogos className="border-b border-white/10 py-10" />
          <section className="py-20" aria-label="Corporate case studies">
            <FadeUp>
              <p className="eyebrow mb-8">Selected engagements</p>
            </FadeUp>
            <div className="border-t border-white/10">
              {list.map((p, i) => (
                <CorporateRow key={p.slug} p={p} i={i} />
              ))}
            </div>
          </section>
        </>
      ) : (
        <section className="py-20" aria-label="Creative case studies">
          <FadeUp className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <p className="eyebrow whitespace-nowrap">Selected works</p>
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter by discipline" data-testid="creative-tag-filter">
              {tags.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tag === t}
                  onClick={() => setTag(t)}
                  data-testid={`tag-filter-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    tag === t ? "border-umber bg-umber text-[#0B0C0E]" : "border-white/15 text-[#9EA3B0] hover:border-white/40 hover:text-white"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </FadeUp>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#616775]" data-testid="creative-result-count">
            {String(shown.length).padStart(2, "0")} {shown.length === 1 ? "project" : "projects"}
          </p>
          <motion.div layout className="mt-8 grid auto-rows-auto gap-6 md:grid-cols-12">
            <AnimatePresence mode="popLayout">
              {shown.map((p, i) => (
                <motion.div
                  key={p.slug}
                  layout
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className={spans[i % 3]}
                >
                  <CreativeCard p={p} i={i} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </section>
      )}

      <FadeUp className="border-t border-white/10 py-16 text-center">
        <p className="text-base text-[#9EA3B0]">This is only half of the house.</p>
        <Link
          to={route === "corporate" ? "/creative" : "/corporate"}
          data-testid="cross-route-link"
          className="mt-4 inline-flex items-center gap-2 border-b border-umber pb-1 font-mono text-[11px] uppercase tracking-[0.3em] text-umber transition-opacity hover:opacity-70"
        >
          {route === "corporate" ? "Cross over to Creative" : "Cross over to Corporate"}
          <ArrowUpRight size={14} />
        </Link>
      </FadeUp>
    </div>
  );
}
